"use client";

import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useRouter } from "next/navigation";
import { Session } from "@supabase/supabase-js";
import { createClient } from "../supabase/client.js";
import { User } from "../types/user.js";
import { USER_ROLES, isEmployeeRole, isAdminRole, isClientRole } from "../constants/user-types.js";
import { setGlobalAuthState, allowAuthContextCall, resetAuthContextCallTracking } from "../supabase/global-auth-guard.js";
import { useSignOut } from "../../hooks/use-sign-out.js";
import { SignOutOptions, SignOutResult } from "../utils/sign-out.js";

// ============================================================================
// SUPABASE AUTH CONTEXT OVERVIEW
// ============================================================================
//
// PURPOSE: Provide Supabase authentication state and user data
//
// FEATURES:
// - Real-time authentication state
// - User profile data from app_users table
// - Role-based access control
// - Automatic session management
//
// ROLE TYPES:
// - wtc_employee: Full access to all features
// - wtc_admin: Full access + admin features
// - wtc_crm: CRM-only access
// - client_user: Limited access to assigned contacts only
//
// ============================================================================

type AppUser = User;

interface SupabaseAuthContextType {
  user: User | null;
  appUser: AppUser | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isEmployee: boolean;
  isAdmin: boolean;
  isClientUser: boolean;
  signOut: (options?: SignOutOptions) => Promise<SignOutResult>;
  refreshUser: () => Promise<void>;
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(
  undefined,
);

interface SupabaseAuthProviderProps {
  children: ReactNode;
}

export const SupabaseAuthProvider = ({
  children,
}: SupabaseAuthProviderProps) => {
  const [user, setUser] = useState<any | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const supabase = createClient();
  
  const isInitializing = useRef(false);
  
  // Use existing deduplication pattern from ApiInterceptor
  const pendingUserFetches = useRef<Map<string, Promise<AppUser | null>>>(new Map());

  // ============================================================================
  // AUTHENTICATION STATE MANAGEMENT
  // ============================================================================

  const fetchAppUser = useCallback(
    async (userId: string, userEmail?: string) => {
      // Check for existing pending request (deduplication)
      const existingRequest = pendingUserFetches.current.get(userId);
      if (existingRequest) {
        console.log("🔍 fetchAppUser deduplicated - returning existing promise for userId:", userId);
        
return existingRequest;
      }
      
      console.log("🔍 fetchAppUser called for userId:", userId);
      
      // Create new request promise
      const requestPromise = (async () => {
        try {
        
        // Get user data from app_users table
        const { data, error } = await supabase
          .from("app_users")
          .select(`
            *,
            roles:role_id (
              name,
              display_name,
              permissions
            )
          `)
          .eq("id", userId)
          .single();

        if (error) {
          console.error("Error fetching app user:", error);
          return null;
        }

        return data as AppUser;
        } catch (error) {
          console.error("Error fetching app user:", error);
          
return null;
        } finally {
          // Clean up pending request
          pendingUserFetches.current.delete(userId);
        }
      })();
      
      // Store pending request for deduplication
      pendingUserFetches.current.set(userId, requestPromise);
      
      return requestPromise;
    },
    [supabase],
  );

  const refreshUser = useCallback(async () => {
    if (user) {
      const appUserData = await allowAuthContextCall(() => 
        fetchAppUser(user.id, user.email)
      );
      if (appUserData) {
        setAppUser(appUserData);
      }
    }
  }, [user]);

  // ============================================================================
  // AUTHENTICATION EVENT HANDLERS
  // ============================================================================

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializing.current) {
      return;
    }
    isInitializing.current = true;
    
    // Reset auth context call tracking
    resetAuthContextCallTracking();
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const appUserData = await allowAuthContextCall(() => 
            fetchAppUser(
              session.user.id,
              session.user.email,
            )
          );
          if (appUserData) {
            setAppUser(appUserData);
          }
        }
      } catch (error: any) {
        // Ignore AbortErrors - they're harmless and occur when operations are cancelled
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          console.log("Auth operation aborted (harmless)");
        } else {
          console.error("Error getting initial session:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log("Auth initialization timeout - forcing loading to complete");
      setIsLoading(false);
    }, 3000); // 3 second timeout - reduced for better UX

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      console.log("Auth state changed:", event, session?.user?.id);

      try {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const appUserData = await allowAuthContextCall(() => 
            fetchAppUser(
              session.user.id,
              session.user.email,
            )
          );
          if (appUserData) {
            setAppUser(appUserData);
          }
        } else {
          setAppUser(null);
        }
      } catch (error: any) {
        // Ignore AbortErrors - they're harmless and occur when operations are cancelled
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          console.log("Auth state change operation aborted (harmless)");
        } else {
          console.error("Error in auth state change:", error);
        }
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
      isInitializing.current = false;
      // Clean up pending requests on unmount
      pendingUserFetches.current.clear();
    };
  }, [supabase.auth, fetchAppUser]);

  // ============================================================================
  // ROLE-BASED COMPUTED PROPERTIES (Memoized for Performance)
  // ============================================================================

  const userRole = useMemo(() => 
    appUser?.roles?.name || appUser?.role, 
    [appUser?.roles?.name, appUser?.role]
  );

  const isAuthenticated = useMemo(() => !!user, [user]);
  const isEmployee = useMemo(() => isEmployeeRole(userRole), [userRole]);
  const isAdmin = useMemo(() => isAdminRole(userRole), [userRole]);
  const isClientUser = useMemo(() => isClientRole(userRole), [userRole]);

  // Update global auth state for the global auth guard
  useEffect(() => {
    setGlobalAuthState({
      isAuthenticated,
      isLoading,
    });
  }, [isAuthenticated, isLoading]);

  // ============================================================================
  // AUTHENTICATION ACTIONS
  // ============================================================================

  const { signOut } = useSignOut();

  // ============================================================================
  // CONTEXT VALUE (Memoized for Performance)
  // ============================================================================

  const value: SupabaseAuthContextType = useMemo(
    () => ({
      user,
      appUser,
      session,
      isLoading,
      isAuthenticated,
      isEmployee,
      isAdmin,
      isClientUser,
      signOut,
      refreshUser,
    }),
    [
      user,
      appUser,
      session,
      isLoading,
      isAuthenticated,
      isEmployee,
      isAdmin,
      isClientUser,
      signOut,
      refreshUser,
    ],
  );

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

// ============================================================================
// HOOK FOR USING THE AUTH CONTEXT
// ============================================================================

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    // Fallback to UniversalAuth if SupabaseAuth not available
    try {
      const { useUniversalAuth } = require('../auth/OptimalAuthProvider');
      return useUniversalAuth();
    } catch (error) {
      throw new Error(
        "useSupabaseAuth must be used within a SupabaseAuthProvider or UniversalAuthProvider",
      );
    }
  }
  
  return context;
};

// ============================================================================
// ROUTE PROTECTION COMPONENTS
// ============================================================================

// Component for protecting routes
interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
  redirectTo?: string;
}

export function SupabaseProtectedRoute({
  children,
  fallback,
  redirectTo = "/login",
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useSupabaseAuth();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !isLoading && !isAuthenticated) {
      const currentPath = window.location.pathname;
      router.push(`${redirectTo}?callbackUrl=${encodeURIComponent(currentPath)}`);
    }
  }, [mounted, isLoading, isAuthenticated, redirectTo, router]);

  if (!mounted || isLoading) {
    return (
      fallback || (
        <div className="flex min-h-screen items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )
    );
  }

  if (!isAuthenticated) {
    return fallback || null;
  }

  return <>{children}</>;
}

// Component for public routes (redirects authenticated users)
interface PublicRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function SupabasePublicRoute({
  children,
  redirectTo = "/unifiedtime",
}: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useSupabaseAuth();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !isLoading && isAuthenticated) {
      router.push(redirectTo);
    }
  }, [mounted, isLoading, isAuthenticated, redirectTo, router]);

  if (!mounted || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
